/**
 * Copyright (c) 2025 rports (r), All rights reserved.
 *
 *     ______    _______  _______  ______    _______  _______ 
 *    |    _ |  |       ||       ||    _ |  |       ||       |
 *    |   | ||  |    _  ||   _   ||   | ||  |_     _||  _____|
 *    |   |_||_ |   |_| ||  | |  ||   |_||_   |   |  | |_____ 
 *    |    __  ||    ___||  |_|  ||    __  |  |   |  |_____  |
 *    |   |  | ||   |    |       ||   |  | |  |   |   _____| |
 *    |___|  |_||___|    |_______||___|  |_|  |___|  |_______|
 *
 * filename      : ContactForm.jsx
 * description   : {{insert here}}
 * creation date : 23/03/2025
 * author        : "Luis CRUZ" <luis.cruz@rports.net>
 */



// --------------------------------------------------------------------------------
//      IMPORTS
// --------------------------------------------------------------------------------

import {
    useState,
    Fragment,
} from 'react';

import {
    Flex,
    View,
    Button,
    Text,
    TextField,
    TextAreaField,
} from '@aws-amplify/ui-react';

import {
    useComponentContext,
    toolKit,
} from '@/components/core';

import {
    format,
} from 'date-fns';

import {
    render,
} from '@react-email/components';

import {
    MessageCustomIcon,
} from './snippets/CustomDiv';

import ContactFormEmail from '@/components/emails/contact-form';



// --------------------------------------------------------------------------------
//      CONSTANTS DEFINITION
// --------------------------------------------------------------------------------

const contactForm_enums_buttonLabel =
{
    SUBMIT: 'SUBMIT',
    RESET: 'RESET',
};



// --------------------------------------------------------------------------------
//      FUNCTIONS
// --------------------------------------------------------------------------------

function ContactForm() {
    // Component context
    const { ctx } = useComponentContext();

    // State variables
    const [nameIsDisabled, setNameIsDisabled] = useState(false);
    const [nameContent, setNameContent] = useState('');
    const [nameIsValid, setNameIsValid] = useState(false);
    const [emailIsDisabled, setEmailIsDisabled] = useState(false);
    const [emailContent, setEmailContent] = useState('');
    const [emailIsValid, setEmailIsValid] = useState(false);
    const [messageIsDisabled, setMessageIsDisabled] = useState(false);
    const [messageMaxSize, /* setMessageMaxSize */] = useState(500);
    const [messageContent, setMessageContent] = useState('');
    const [buttonIsDisabled, setButtonIsDisabled] = useState(true);
    const [buttonIsLoading, setButtonIsLoading] = useState(false);
    const [buttonLabel, setButtonLabel] = useState(contactForm_enums_buttonLabel.SUBMIT);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);

    function handleOnChange_button(nameIsValid, emailIsValid) {
        // Validate the content
        setButtonIsDisabled(!nameIsValid || !emailIsValid);
    };

    function handleOnChange_name(e) {
        const content = e.currentTarget.value;

        // Set the content
        setNameContent(content);

        // Validate the content
        const isValid = '' !== String(content).trim();

        setNameIsValid(isValid);

        // Propagate the event
        handleOnChange_button(isValid, emailIsValid);
    };

    function handleOnChange_email(e) {
        const content = e.currentTarget.value;

        // Set the content
        setEmailContent(content);

        // Validate the content
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        const isValid = re.test(String(content).trim().toLowerCase());

        setEmailIsValid(isValid);

        // Propagate the event
        handleOnChange_button(nameIsValid, isValid);
    };

    function handleOnChange_message(e) {
        const content = e.target.value;

        // Set the content
        setMessageContent(content);
    };

    async function handle_sendEmail(name, email, message) {
        const timestamp = new Date();

        //
        // Email subject
        //
        // Useful link:
        // - https://date-fns.org/v4.1.0/docs/parse
        const emailSubject = format(timestamp, "'New customer message on' iiii MMMM d, yyyy 'at' kk:mm:ss 'GMT'xx");

        //
        // Bcc list
        //
        const BccList =
            [
                'STAURIX <contact@staurix.com>',
                ...(toolKit.enums.runtime.configTypes.PRODUCTION === ctx.runtime.config) ?
                    // Production mode
                    [
                        'Marc SALTON <marc.salton@rports.net>',
                        'Alejandro ARREDONDO <alejandro.arredondo@rports.net>',
                    ]
                    :
                    // Development mode
                    [
                        // Keep empty for eventual future use
                    ],
            ];

        //
        // Body content, in HTML format
        //
        const renderedBodyContent = await render(
            <ContactFormEmail
                name={name}
                email={email}
                message={message}
                timestamp={timestamp}
            />
        );

        //
        // Email arguments
        //
        const emailArgs =
        {
            emailSubject: emailSubject,
            bodyContent: renderedBodyContent,
            replyToList:
                [
                    // Keep empty for eventual future use
                ],
            destinationList:
                [
                    process.env.REACT_APP__AWS_SDK__SES__MAILER__CONTACT,
                ],
            CcList:
                [
                    // Keep empty for eventual future use
                ],
            BccList:
                [
                    ...BccList,
                ],
        }

        // Trigger the sendEmail async action
        const response = await toolKit.api.email.sendEmail(emailArgs);

        return response;
    }

    async function handleOnClick_submit() {
        // Process event: Submit
        if (contactForm_enums_buttonLabel.SUBMIT === buttonLabel) {
            const name = nameContent.trim();
            const email = emailContent.trim();
            const message = messageContent;

            // Set the state
            setNameIsDisabled(true);
            setEmailIsDisabled(true);
            setMessageIsDisabled(true);
            setButtonIsDisabled(true);
            setButtonIsLoading(true);

            // Trigger the sendEmail async action
            const response = await handle_sendEmail(name, email, message);

            // Reset the state
            setButtonIsDisabled(false);
            setButtonIsLoading(false);
            setButtonLabel(contactForm_enums_buttonLabel.RESET);

            // Process the response
            if (toolKit.enums.email.statusTypes.SUCCESS === response.status) {
                // Show the success message
                setShowSuccessMessage(true);

                // TODO: Store result in database
                // console.log(response.data);
            }
            else {
                // Show the error message
                setShowErrorMessage(true);

                // TODO: Store result in database
                // console.error(response.data, response.data.stack);
            }
        }

        // Process event: Reset
        else {
            // Set the state
            setNameIsDisabled(false);
            setNameContent('');
            setNameIsValid(false);
            setEmailIsDisabled(false);
            setEmailContent('');
            setEmailIsValid(false);
            setMessageIsDisabled(false);
            setMessageContent('');
            setButtonIsDisabled(true);
            setButtonLabel(contactForm_enums_buttonLabel.SUBMIT);
            setShowSuccessMessage(false);
            setShowErrorMessage(false);
        }
    }

    return (
        <Fragment>
            <View
                className='contact-form-layout'
            >
                <View
                    className='contact-form-container'
                >
                    <View
                        className='contact-form-router'
                    >
                        <Flex
                            as='form'
                            width={ctx.theme.tokens.space.relative.full}
                            gap={ctx.theme.tokens.space.zero}
                            direction='column'
                        >
                            <Flex
                                width={ctx.theme.tokens.space.relative.full}
                                direction={
                                    ctx.api.utils.adaptiveOption(
                                        'row',
                                        'row',
                                        'column'
                                    )
                                }
                                gap={ctx.theme.tokens.space.zero}
                            >
                                <Flex
                                    width={
                                        ctx.api.utils.adaptiveOption(
                                            '50%',
                                            '50%',
                                            ctx.theme.tokens.space.relative.full
                                        )
                                    }
                                    paddingTop={ctx.theme.tokens.space.xs}
                                    paddingBottom={ctx.theme.tokens.space.xs}
                                    paddingRight={
                                        ctx.api.utils.adaptiveOption(
                                            ctx.theme.tokens.space.medium,
                                            ctx.theme.tokens.space.medium,
                                            ctx.theme.tokens.space.zero
                                        )
                                    }
                                    paddingLeft={ctx.theme.tokens.space.zero}
                                >
                                    <TextField
                                        isDisabled={nameIsDisabled}
                                        label={
                                            <Text>
                                                <Text
                                                    as='span'
                                                    fontWeight={ctx.theme.tokens.fontWeights.bold}
                                                    fontSize={ctx.theme.tokens.fontSizes.large}
                                                >
                                                    {/* TODO: Translate message */}
                                                    Name
                                                </Text>

                                                <Text
                                                    as='span'
                                                    fontSize={ctx.theme.tokens.fontSizes.small}
                                                    color='red'
                                                >
                                                    {' '}
                                                    {/* TODO: Translate message */}
                                                    (required)
                                                </Text>
                                            </Text>
                                        }
                                        type='text'
                                        width={ctx.theme.tokens.space.relative.full}
                                        value={nameContent}
                                        onChange={handleOnChange_name}
                                        hasError={!nameIsValid}
                                    />
                                </Flex>

                                <Flex
                                    width={
                                        ctx.api.utils.adaptiveOption(
                                            '50%',
                                            '50%',
                                            ctx.theme.tokens.space.relative.full
                                        )
                                    }
                                    paddingTop={ctx.theme.tokens.space.xs}
                                    paddingBottom={ctx.theme.tokens.space.xs}
                                    paddingRight={ctx.theme.tokens.space.zero}
                                    paddingLeft={
                                        ctx.api.utils.adaptiveOption(
                                            ctx.theme.tokens.space.medium,
                                            ctx.theme.tokens.space.medium,
                                            ctx.theme.tokens.space.zero
                                        )
                                    }
                                >
                                    <TextField
                                        isDisabled={emailIsDisabled}
                                        label={
                                            <Text>
                                                <Text
                                                    as='span'
                                                    fontWeight={ctx.theme.tokens.fontWeights.bold}
                                                    fontSize={ctx.theme.tokens.fontSizes.large}
                                                >
                                                    {/* TODO: Translate message */}
                                                    Email
                                                </Text>

                                                <Text
                                                    as='span'
                                                    fontSize={ctx.theme.tokens.fontSizes.small}
                                                    color='red'
                                                >
                                                    {' '}
                                                    {/* TODO: Translate message */}
                                                    (required)
                                                </Text>
                                            </Text>
                                        }
                                        type='email'
                                        width={ctx.theme.tokens.space.relative.full}
                                        value={emailContent}
                                        onChange={handleOnChange_email}
                                        hasError={!emailIsValid}
                                    />
                                </Flex>
                            </Flex>

                            <TextAreaField
                                isDisabled={messageIsDisabled}
                                labelHidden={true}
                                placeholder='Message' // TODO: Translate message
                                rows={10}
                                maxLength={messageMaxSize}
                                value={messageContent}
                                onChange={handleOnChange_message}
                                paddingTop={ctx.theme.tokens.space.small}
                                paddingRight={ctx.theme.tokens.space.zero}
                                paddingLeft={ctx.theme.tokens.space.zero}
                            />

                            <Text
                                as='span'
                                variation='tertiary'
                                fontSize={ctx.theme.tokens.fontSizes.xs}
                                textAlign='right'
                                paddingTop={ctx.theme.tokens.space.xxs}
                                paddingBottom={ctx.theme.tokens.space.small}
                            >
                                {messageContent.length} / {messageMaxSize}
                            </Text>

                            {(showSuccessMessage) &&
                                <Fragment>
                                    <MessageCustomIcon
                                        variation='filled'
                                        colorTheme='success'
                                        heading='Thanks for contacting us. We will get back to you as soon as possible.' // TODO: Translate message
                                        marginTop={ctx.theme.tokens.space.small}
                                        marginBottom={ctx.theme.tokens.space.medium}
                                    />
                                </Fragment>
                            }

                            {(showErrorMessage) &&
                                <Fragment>
                                    <MessageCustomIcon
                                        variation='filled'
                                        colorTheme='error'
                                        heading='An error has occurred trying to process your request. Please try again later.' // TODO: Translate message
                                        marginTop={ctx.theme.tokens.space.small}
                                        marginBottom={ctx.theme.tokens.space.medium}
                                    />
                                </Fragment>
                            }

                            <Flex
                                width={ctx.theme.tokens.space.relative.full}
                                gap={ctx.theme.tokens.space.zero}
                                justifyContent='center'
                                paddingTop={ctx.theme.tokens.space.small}
                                paddingBottom={ctx.theme.tokens.space.small}
                            >
                                <Button
                                    variation='primary'
                                    isLoading={buttonIsLoading}
                                    isDisabled={buttonIsDisabled}
                                    loadingText='Loading...' // TODO: Translate message
                                    width={ctx.theme.tokens.space.relative.full}
                                    onClick={handleOnClick_submit}
                                >
                                    {/* TODO: Translate message */}
                                    {buttonLabel}
                                </Button>
                            </Flex>
                        </Flex>
                    </View>

                    <View
                        height={ctx.theme.tokens.space.xs}
                    />
                </View>
            </View>
        </Fragment>
    );
}



// --------------------------------------------------------------------------------
//      EXPORTED OBJECTS
// --------------------------------------------------------------------------------

export default ContactForm;



// End of file
